// src/hooks/useProjectSave.js
import { useCallback, useEffect, useRef } from 'react';
import * as C from '../constants';
import api from '../api';
import projectCacheService from '../services/projectCacheService';

/**
 * 项目保存、自动保存、页面离开保存
 *
 * ══════════════════════════════════════════════════════
 * 安全机制总览（共 6 重保护）：
 *
 *   1. isSavingRef：防止并发保存（同一时刻只允许一个 save 在途）
 *      → 自动保存直接跳过，手动保存等待最多 5s
 *
 *   2. isMountedRef：防止幽灵保存（组件卸载后 setTimeout 触发）
 *      → ReactFlowProvider 已销毁，getNodes() 返回空数组，保存会清空数据库
 *
 *   3. activeProjectIdRef：防止跨项目保存（闭包中的 projectId 是旧值）
 *      → 场景：用户从项目 A 切换到 B，项目 A 的 setTimeout 回调会用 B 的数据覆盖 A
 *
 *   4. 异步边界保护：saveProject 每个 await 之后、操作共享状态之前，
 *      重新检查 activeProjectIdRef === projectId
 *      → 防止 fetch 等待期间项目切换导致 version/dirty 污染
 *
 *   5. 空节点保护：cleanNodes.length === 0 但 nodesRef 有数据
 *      → 说明 ReactFlowProvider 已销毁或状态不一致，跳过保存
 *
 *   6. 版本号乐观并发控制（version）
 *      → 409 冲突时自动同步服务端版本并重试
 * ══════════════════════════════════════════════════════
 *
 * 保存触发方式：
 *   - 自动保存：nodes/edges 变化后 2s debounce
 *   - 手动保存：用户点击保存按钮 / Ctrl+S
 *   - 离开保存：beforeunload（刷新/关闭）+ 组件卸载（路由导航）
 *
 * 离开保存特殊处理（syncSaveRef）：
 *   - 不能使用 async/await（beforeunload 不等待异步）
 *   - 使用 fetch + keepalive: true（确保页面关闭后请求仍能发出）
 *   - 直接用 raw fetch（不经过 api.authFetch，避免额外的 async 层级）
 *
 * @param {object} deps — 依赖注入（见下方 JSDoc）
 * @returns {{ saveProject: Function, syncSaveRef: object, isMountedRef: object, activeProjectIdRef: object }}
 */
const useProjectSave = (deps) => {
  const {
    projectId, getNodes, getEdges, getViewport,
    nodesRef, projectVersionRef, skipNextAutoSaveRef, isDataReadyRef, isReadOnlyRef,
    isInitialized, nodes, edges, setAlertMsg,
    crewIdRef, crewNameRef,
  } = deps;

  // 🛡️ 防止并发保存（同一时刻只允许一个 save 请求在途）
  const isSavingRef = useRef(false);
  // 🛡️ 保存锁 Promise：手动保存时 await 此 Promise 而非轮询
  const saveLockResolveRef = useRef(null);
  const saveLockPromiseRef = useRef(null);
  // 🛡️ 追踪组件生命周期，防止卸载后的幽灵保存（setTimeout 回调用空数组覆盖数据库）
  const isMountedRef = useRef(true);
  // 🛡️ 当前活跃的 projectId，用于检测跨会话幽灵保存
  const activeProjectIdRef = useRef(projectId);
  // 🛡️ 记录最近一次成功保存时的节点数量，防止 unmount 后 nodesRef 已清空导致空节点保护失效
  const lastKnownGoodNodeCountRef = useRef(0);

  // ─�� buildCleanNodes: 构建可序列化的干净节点数据 ──
  // 剔除不能持久化的字段：
  //   - localCacheUrl：blob URL，会话结束即失效
  //   - uploading：瞬态字段，不应保存到数据库
  //   - blob:/data: 开头的 output：无法跨会话恢复
  //   - annotationPreview：blob URL，由 annotationData 重新渲染
  //   - 函数/ref：回调函数和 React ref 不能 JSON 序列化也不能 structured clone
  //
  // 为什么要同时使用 getNodes() 和 nodesRef.current？
  //   - getNodes() 返回 React Flow 内部的节点（包含位置、选中状态等）
  //   - nodesRef.current 是最新的 React state（包含回调函数、完整 data）
  //   - 合并两者：用 getNodes 的位置/尺寸 + nodesRef 的 data

  // 🧹 剥离 data 中的函数和 ref 对象（onUpdate、onGenerate、globalAssetsRef 等）
  // JSON.stringify 会静默忽略函数，但 IndexedDB structured clone 会报错
  const stripNonSerializable = useCallback((data) => {
    const result = {};
    for (const key in data) {
      const val = data[key];
      if (typeof val === 'function') continue;
      // React ref 对象 { current: ... } — 如 globalAssetsRef，不应持久化
      if (val && typeof val === 'object' && !Array.isArray(val) && Object.keys(val).length === 1 && 'current' in val) continue;
      result[key] = val;
    }
    return result;
  }, []);

  const buildCleanNodes = useCallback(() => {
    const flowNodes = getNodes();
    const latestNodes = nodesRef.current;
    const stateNodesMap = new Map(latestNodes.map(n => [n.id, n]));

    return flowNodes.map(node => {
      const { selected, positionAbsolute, dragging, ...rest } = node;
      const stateNode = stateNodesMap.get(node.id);

      if (stateNode) {
        const { localCacheUrl, uploading, ...rawData } = stateNode.data;
        const cleanData = stripNonSerializable(rawData);
        if (cleanData.output && (cleanData.output.startsWith('blob:') || cleanData.output.startsWith('data:'))) {
          cleanData.output = undefined;
          cleanData.outputs = undefined;
        }
        // 🔧 annotationPreview 是 blob URL，不能持久化，项目加载时从 annotationData 重新渲染
        if (cleanData.annotationPreview && cleanData.annotationPreview.startsWith('blob:')) {
          delete cleanData.annotationPreview;
        }
        if (cleanData.refFiles) cleanData.refFiles = cleanData.refFiles.map(({ localCacheUrl: _, ...f }) => f);
        if (cleanData.refAudios) cleanData.refAudios = cleanData.refAudios.map(({ localCacheUrl: _, ...a }) => a);
        return { ...rest, data: cleanData };
      }

      const { localCacheUrl, uploading, ...rawData } = rest.data || {};
      const cleanData = stripNonSerializable(rawData);
      if (cleanData.output && (cleanData.output.startsWith('blob:') || cleanData.output.startsWith('data:'))) {
        cleanData.output = undefined;
        cleanData.outputs = undefined;
      }
      // 🔧 同上
      if (cleanData.annotationPreview && cleanData.annotationPreview.startsWith('blob:')) {
        delete cleanData.annotationPreview;
      }
      if (cleanData.refFiles) cleanData.refFiles = cleanData.refFiles.map(({ localCacheUrl: _, ...f }) => f);
      if (cleanData.refAudios) cleanData.refAudios = cleanData.refAudios.map(({ localCacheUrl: _, ...a }) => a);
      return { ...rest, data: cleanData };
    });
  }, [getNodes, nodesRef, stripNonSerializable]);

  // ── saveProject: 异步保存（支持手动/自动） ──
  // 版本冲突处理流程：
  //   1. 发送 PUT 带当前 version
  //   2. 服务端返回 200 → 更新本地 version
  //   3. 服务端返回 409 → 取服务端最新 version 并立即重试
  //   4. 重试也失败 → GET 获取最新 version，等待用户下次保存
  const saveProject = useCallback(async (isManual = false) => {
    if (!projectId) return true;

    // 🛡️ 组件已卸载 → 跳过保存（ReactFlowProvider 已销毁，getNodes() 返回空数组）
    if (!isMountedRef.current) {
      console.log('🛡️ 组件已卸载，跳过保存');
      return true;
    }

    // 🛡️ 数据未就绪：Plan A 缓存秒开渲染了过期 nodes，但 Plan B 服务端数据尚未到达。
    //   此时若保存，会用过期 nodes + 当前 version 写回服务器，把新数据覆盖回旧版本。
    //   场景：从首页/管理面板回到 editor，Plan B 网络慢；同时用户操作触发了保存。
    if (isDataReadyRef && isDataReadyRef.current === false) {
      console.log('🛡️ 数据未就绪（服务端权威数据尚未加载完毕），跳过保存');
      return true;
    }

    // 🔒 只读 tab：另一标签页/窗口已接管编辑权，本 tab 禁止任何保存路径。
    if (isReadOnlyRef && isReadOnlyRef.current === true) {
      console.log('🔒 当前 tab 为只读（另一窗口已接管），跳过保存');
      if (isManual) setAlertMsg({ text: '另一标签页已接管此图板,本页面只读。请在原页面继续编辑,或点顶部"在此处接管"。', type: 'warning' });
      return true;
    }

    // 🛡️ 版本号哨兵：Plan A 缓存秒开后用 -1 标记"未同步权威版本"，
    //   一定要等 Plan B 写入真实 version 后才能保存。纵深防御 isDataReadyRef。
    if (projectVersionRef.current < 0) {
      console.log('🛡️ projectVersion 尚未从服务端同步，跳过保存');
      return true;
    }

    // 🛡️ 跨会话防护：闭包中的 projectId 与当前活跃 projectId 不匹配
    // 原因：React Router 复用组件或 setTimeout 回调携带旧闭包时，
    // saveProject 会用新项目的 nodes/version 写入旧项目的数据库记录
    if (activeProjectIdRef.current && projectId !== activeProjectIdRef.current) {
      console.warn(`🛡️ 跨项目保存拦截: 闭包=${projectId}, 当前=${activeProjectIdRef.current}`);
      return true;
    }

    // 🛡️ 防止并发：如果已有保存在途，跳过本次（自动保存）或排队等待（手动保存）
    if (isSavingRef.current) {
      if (!isManual) {
        console.log('⏳ 跳过自动保存（有保存正在进行）');
        return true;
      }
      // 手动保存：await 当前保存的 Promise（最多等 5 秒），替代低效的轮询
      if (saveLockPromiseRef.current) {
        await Promise.race([
          saveLockPromiseRef.current,
          new Promise(r => setTimeout(r, 5000)),
        ]);
      }
    }

    isSavingRef.current = true;
    // 创建锁 Promise，供后续并发的手动保存 await
    saveLockPromiseRef.current = new Promise(resolve => {
      saveLockResolveRef.current = resolve;
    });
    try {
      const cleanNodes = buildCleanNodes();

      // 🛡️ 空节点保护：如果清洁后的节点为空但最新 React 状态有节点，
      // 说明 ReactFlowProvider 已销毁或状态不一致，跳过保存防止空数据覆盖
      if (cleanNodes.length === 0 && nodesRef.current && nodesRef.current.length > 0) {
        console.warn('🛡️ 检测到空节点保存，跳过（未初始化或组件已卸载）');
        return true;
      }

      const payload = {
        nodes: cleanNodes,
        edges: getEdges(),
        viewport: getViewport(),
        version: projectVersionRef.current,
        _trigger: isManual ? 'manual_save' : 'auto_save',
      };

      // 🛡️ 提交前 revalidate：防止"过期客户端"用陈旧 ReactFlow store 覆盖服务端
      // 触发场景：
      //   - 多 tab：旧 tab 的 ReactFlow store 是几小时前的快照（80 节点），
      //     当前 tab 已编辑成 24 节点。旧 tab 触发 save 时，version 会被 WS 同步成最新，
      //     payload = 80 节点 + 最新 version → 通过 OCC → 旧数据覆盖新数据。
      //   - 长时间挂起：tab 在后台 30 分钟，期间其他设备/会话改动了项目。
      // 校验逻辑（必须双重）：
      //   1) 本客户端的 projectVersionRef 必须 ≥ 服务端 version；
      //      如果服务端 version 比本地大很多，说明本地持有的是陈旧 store，必须中止。
      //   2) 节点数膨胀保护：如果 incoming 节点数比 server 多 30% 以上（且超过 5 个），
      //      多半是陈旧 store 苏醒（version 被 WS 偷偷同步过，OCC 已失效）。拒绝写入。
      try {
        const headRes = await api.authFetch(`${C.API_BASE_URL}/api/projects/${projectId}/version`, { method: 'GET' });
        if (headRes.ok) {
          const headJson = await headRes.json();
          const serverVersion = Number(headJson?.version);
          const serverNodeCount = Number(headJson?.nodeCount);
          if (Number.isFinite(serverVersion) && serverVersion > projectVersionRef.current) {
            console.warn(`🛡️ revalidate 拦截: server=${serverVersion} 比本地=${projectVersionRef.current} 新，拒绝过期保存`);
            if (isManual) {
              setAlertMsg({ text: '本地数据已过期（其他会话已编辑），请刷新后再保存', type: 'warning' });
            }
            return false;
          }
          // 🛡️ 节点数膨胀检测：incoming 比 server 多 30% 且差额 ≥5 个 → 疑似陈旧 store
          //   场景：Tab B（多 tab/后台挂起）持有 80 节点旧 store，
          //         server 是 26 节点，version 已被 WS 同步成一致。
          //         OCC 拦不住，但节点数差异极大 → 这里拦住。
          //
          // ⚠️ 误伤修复（2026-06-29）：单纯比 server 多 ≠ 陈旧 store。
          //   分镜大师批量生成、粘贴多节点等「本 tab 合法新增」也会让本地远多于 server
          //   （server 还没来得及落库）。旧逻辑一刀切拒绝 → 自动保存也被静默挡掉 →
          //   新节点永远进不了库 → 越加越多越锁死 → 刷新后全部丢失（你正中此坑）。
          //   区分关键：lastKnownGoodNodeCountRef 是本 tab 成功保存过的节点数。
          //     · 本地 > 历史保存值 → 这些节点是本 tab 主动新增（合法）→ 放行；
          //     · 本地 ≤ 历史保存值 且仍远多于 server → 本 tab 没新增却虚胖 → 真·陈旧快照 → 拦截。
          //   lastKnownGood=0（本会话尚未成功保存过）时一律放行：刚加载的 tab 不可能是
          //   「挂起数小时的陈旧 store」，且 version 校验已先行兜住"其他会话已编辑"的情况。
          const grewBeyondThisTab =
            lastKnownGoodNodeCountRef.current <= 0
            || cleanNodes.length > lastKnownGoodNodeCountRef.current;
          if (Number.isFinite(serverNodeCount)
              && serverNodeCount > 0
              && cleanNodes.length > serverNodeCount * 1.3
              && (cleanNodes.length - serverNodeCount) >= 5
              && !grewBeyondThisTab) {
            console.warn(`🛡️ revalidate 拦截: incoming=${cleanNodes.length} 比 server=${serverNodeCount} 多很多且未超过本 tab 历史值(${lastKnownGoodNodeCountRef.current})，疑似陈旧 store`);
            if (isManual) {
              setAlertMsg({ text: '检测到本地数据异常膨胀，已拒绝保存。请刷新页面以同步最新数据。', type: 'warning' });
            }
            return false;
          }
        }
      } catch (revErr) { /* GET 失败不阻止保存，让 OCC 兜底 */ }

      // 🛡️ 使用 authFetch 直接控制响应处理，避免 checkResponse 在 409 时抛错
      const url = `${C.API_BASE_URL}/api/projects/${projectId}`;
      const res = await api.authFetch(url, { method: 'PUT', body: payload });

      if (res.ok) {
        const result = await res.json();
        // 🛡️ 异步边界保护：fetch 等待期间项目可能已切换
        // 如果不检查，旧项目的版本号会覆盖新项目的 projectVersionRef，
        // 导致新项目下次保存必定 409 冲突
        if (activeProjectIdRef.current === projectId) {
          if (result?.version !== undefined) projectVersionRef.current = result.version;
          dirtyRef.current = false;
          if (payload.nodes.length > 0) lastKnownGoodNodeCountRef.current = payload.nodes.length;
          try {
            projectCacheService.put(projectId, {
              nodes: payload.nodes,
              edges: payload.edges,
              viewport: payload.viewport,
              version: result?.version ?? payload.version,
              crewId: crewIdRef?.current,
              crewName: crewNameRef?.current,
            });
          } catch (e) { /* 缓存写入失败不影响保存结果 */ }
          if (isManual) setAlertMsg({ text: "项目已保存", type: "success" });
        } else {
          console.log(`🛡️ 保存成功但项目已切换 (${projectId}→${activeProjectIdRef.current}), 跳过共享状态更新`);
        }
        return true;
      }

      // 🛡️ 409 版本冲突处理：同步服务端版本号并自动重试
      if (res.status === 409) {
        let serverData;
        try { serverData = await res.json(); } catch (e) { /* ignore */ }
        const serverVersion = serverData?.serverVersion;
        const conflictCode = serverData?.code;
        console.warn(`⚠️ 版本冲突: client=${payload.version}, server=${serverVersion}, code=${conflictCode}`);

        // 🛡️ 数据保护拦截（节点骤减 / 空节点 / 异常膨胀）：不要重试！重试还是同样陈旧数据
        // 自动保存 → 静默标记本 tab 异常，停止后续 dirty 触发
        // 手动保存 → 弹窗让用户确认是否强制覆盖
        if (conflictCode === 'NODE_COUNT_DROP_REJECTED'
            || conflictCode === 'EMPTY_NODES_REJECTED'
            || conflictCode === 'NODE_COUNT_GROWTH_REJECTED') {
          const reason = serverData?.error || '数据保护拦截';
          console.warn(`🛡️ 服务端拒绝保存 [${conflictCode}]: ${reason}`);
          // 锁住本 tab 的脏标记，避免反复重发
          dirtyRef.current = false;
          if (isManual) {
            // 手动保存：明确告知，建议用户刷新或在新窗口重新打开
            setAlertMsg({
              text: `保存被阻止：${reason}`,
              type: 'error'
            });
          } else {
            // 自动保存：弹一次警告横幅；用户自己决定要不要刷新
            setAlertMsg({
              text: `自动保存已暂停：${reason}`,
              type: 'warning'
            });
          }
          return false;
        }

        if (serverVersion !== undefined) {
          // 🛡️🔴 409 重试前检查：如果用户已切换到其他图板，放弃重试和版本同步！
          // 场景复现：保存项目 A → 409 冲突 → 用户已切换到项目 B →
          // buildCleanNodes() 读取的是项目 B 的节点 → 写入项目 A 的 URL → 数据污染
          // 版本同步也必须拦截：否则旧项目的 serverVersion 会覆盖新项目的 projectVersionRef
          if (activeProjectIdRef.current && projectId !== activeProjectIdRef.current) {
            console.warn(`🛡️ 409 处理中止: 用户已切换项目 (闭包=${projectId}, 当前=${activeProjectIdRef.current})`);
            return false;
          }

          // 🛡️ 重建 payload：409 期间可能收到了 node-updated 事件（AI 生成完成），
          // nodesRef.current 已包含最新数据，用旧 payload 重试会覆盖 AI 结果
          const freshNodes = buildCleanNodes();
          const retryPayload = {
            nodes: freshNodes,
            edges: getEdges(),
            viewport: getViewport(),
            version: serverVersion,
            _trigger: isManual ? 'manual_save' : 'auto_save',
          };

          const retryRes = await api.authFetch(url, { method: 'PUT', body: retryPayload });
          if (retryRes.ok) {
            const retryResult = await retryRes.json();
            // 🛡️ 异步边界保护：409 重试期间项目可能已切换
            if (activeProjectIdRef.current === projectId) {
              if (retryResult?.version !== undefined) projectVersionRef.current = retryResult.version;
              dirtyRef.current = false;
              if (retryPayload.nodes.length > 0) lastKnownGoodNodeCountRef.current = retryPayload.nodes.length;
              try {
                projectCacheService.put(projectId, {
                  nodes: retryPayload.nodes, edges: retryPayload.edges,
                  viewport: retryPayload.viewport, version: retryResult?.version ?? retryPayload.version,
                  crewId: crewIdRef?.current,
                  crewName: crewNameRef?.current,
                });
              } catch (e) { /* 缓存写入失败不影响保存结果 */ }
              if (isManual) setAlertMsg({ text: "项目已保存", type: "success" });
            } else {
              console.log(`🛡️ 409重试成功但项目已切换, 跳过共享状态更新`);
            }
            console.log('✅ 版本冲突已自动解决，保存成功');
            return true;
          }
        }

        // 重试也失败或没拿到 serverVersion → 最后兜底：从 GET 获取最新版本
        // 🛡️ 异步边界保护：仅在项目未切换时才更新版本号
        if (activeProjectIdRef.current === projectId) {
          try {
            const projectData = await api.get(`/api/projects/${projectId}`);
            if (projectData?.version !== undefined) {
              projectVersionRef.current = projectData.version;
              console.log(`🔄 已从服务端同步版本号: ${projectData.version}`);
            }
          } catch (e) { /* ignore */ }
        }

        if (isManual) setAlertMsg({ text: "保存冲突已自动解决，请再试一次", type: "warning" });
        return false;
      }

      // 其他错误
      let errorMsg = `HTTP ${res.status}`;
      try { const d = await res.json(); errorMsg = d.error || d.message || errorMsg; } catch (e) {}
      throw new Error(errorMsg);

    } catch (err) {
      console.error("保存项目失败", err);
      if (isManual) setAlertMsg({ text: "保存项目失败", type: "error" });
      return false;
    } finally {
      isSavingRef.current = false;
      // 释放锁 Promise，唤醒等待中的手动保存
      if (saveLockResolveRef.current) {
        saveLockResolveRef.current();
        saveLockResolveRef.current = null;
        saveLockPromiseRef.current = null;
      }
    }
  }, [projectId, buildCleanNodes, getEdges, getViewport, projectVersionRef, setAlertMsg]);

  // ── 自动保存：脏标记 + 固定间隔检查模式 ──
  // 重要优化：不再将 nodes/edges 作为 useEffect 依赖（每次按键都会触发 cleanup/setup），
  // 改为：nodes/edges 变化 → 标记脏位 → 固定 2 秒间隔检查脏位并保存
  const dirtyRef = useRef(false);
  const prevNodesRef = useRef(nodes);
  const prevEdgesRef = useRef(edges);

  // 检测 nodes/edges 引用变化 → 标记为脏
  if (nodes !== prevNodesRef.current || edges !== prevEdgesRef.current) {
    prevNodesRef.current = nodes;
    prevEdgesRef.current = edges;
    if (isInitialized && projectId) {
      if (skipNextAutoSaveRef.current) {
        skipNextAutoSaveRef.current = false;
      } else if (isDataReadyRef && isDataReadyRef.current === false) {
        // 🛡️ 服务端权威数据尚未加载完毕（Plan A 缓存秒开阶段），
        //   此时 nodes 可能是 IndexedDB 缓存中的过期快照，禁止标脏，
        //   防止旧 nodes + 当前 version 写回服务器覆盖新数据。
      } else if (isReadOnlyRef && isReadOnlyRef.current === true) {
        // 🔒 只读 tab：被动 nodes 变化（如 selection、拖拽视觉态）也不应标脏
      } else {
        dirtyRef.current = true;
      }
    }
  }

  // 固定间隔检查脏标记（30秒），仅在脏时才保存
  // 兜底机制：Ctrl+S 手动保存 + 切换/退出图板保存已覆盖绝大多数场景
  useEffect(() => {
    if (!isInitialized || !projectId) return;
    const interval = setInterval(() => {
      if (dirtyRef.current) {
        dirtyRef.current = false;
        saveProject(false).then(ok => {
          if (!ok && activeProjectIdRef.current === projectId) {
            dirtyRef.current = true; // 保存失败 → 重标脏，下个周期重试
          }
        });
      }
    }, 30000);
    return () => clearInterval(interval);
  }, [isInitialized, projectId, saveProject]);

  // ── syncSaveRef: 同步保存（用于 beforeunload / 组件卸载） ──
  // 为什么不能用 saveProject（async）？
  //   - beforeunload 事件不等待 Promise，异步请求会被取消
  //   - 必须用 synchronous fetch + keepalive: true
  //
  // 为什么用 syncSaveRef.current 而不是直接定义函数？
  //   - 每次渲染都重新赋值 syncSaveRef.current，确保闭包中的变量是最新的
  //   - 但 useEffect cleanup 中的引用不变（syncSaveRef 本身是稳定的）
  //
  // activeProjectIdRef 的作用：
  //   - 当 projectId 从 A 变为 B 时，cleanup 先于 setup 执行
  //   - cleanup 时 activeProjectIdRef 仍为 A（正确！）
  //   - 但闭包中的 projectId 已经是 B（错误！）
  //   - 所以用 activeProjectIdRef.current 而不是闭包 projectId
  const syncSaveRef = useRef(null);
  syncSaveRef.current = () => {
    // 🛡️ 使用 activeProjectIdRef 而非闭包中的 projectId
    // 原因：syncSaveRef.current 在每次渲染时被重新赋值，当 projectId 从 A 变为 B 时，
    // 闭包中的 projectId 已经是 B，但 cleanup 中需要保存的是项目 A 的数据。
    // activeProjectIdRef 在 cleanup 中仍为 A（cleanup 先于 setup），确保保存到正确项目。
    const pid = activeProjectIdRef.current;
    if (!isInitialized || !pid) return;

    // 🛡️ 数据未就绪：Plan B 尚未把服务端权威 nodes 注入，syncSave 出去会回写过期数据
    if (isDataReadyRef && isDataReadyRef.current === false) {
      console.log('🛡️ syncSave: 数据未就绪，跳过离开保存');
      return;
    }

    // 🔒 只读 tab：本页面已让位给另一会话，syncSave 也不能再发
    if (isReadOnlyRef && isReadOnlyRef.current === true) {
      console.log('🔒 syncSave: 当前 tab 只读，跳过离开保存');
      return;
    }

    // 🛡️ 版本哨兵：Plan A 缓存秒开后 projectVersionRef=-1，必须等 Plan B 同步真值。
    //   纵深防御 isDataReadyRef，避免任何异步窗口让 -1 漏出去。
    if (projectVersionRef.current < 0) {
      console.log('🛡️ syncSave: projectVersion 未同步，跳过');
      return;
    }

    // 🛡️ 如果有 saveProject 正在进行，跳过离开保存
    // 原因：saveProject 和 syncSave 会各自带同一个 version 发请求，
    // saveProject 先到达服务端后会递增 version，导致 syncSave 的 version 过时 → 409 冲突。
    // 在途的 saveProject 已经会完成数据持久化，无需重复保存。
    if (isSavingRef.current) {
      console.log('🛡️ syncSave: 跳过离开保存（saveProject 正在进行中）');
      return;
    }

    // 🛡️ Risk 3 修复：如果没有脏数据，跳过冗余保存
    // 场景：onSaveBeforeSwitch 刚成功保存 → dirtyRef 已在 interval 中清零 →
    // 紧接着 cleanup 触发 syncSave → 带着新 version 发同样数据 → 浪费请求或触发 409
    if (!dirtyRef.current) {
      console.log('🛡️ syncSave: 数据未变更，跳过冗余保存');
      return;
    }
    try {
      const rawNodes = getNodes();

      // 🛡️ 空节点保护：不用空数据覆盖有效数据
      // lastKnownGoodNodeCountRef 防止 unmount 后 nodesRef 已清空导致保护失效
      if (rawNodes.length === 0 && (
        (nodesRef.current && nodesRef.current.length > 0) ||
        lastKnownGoodNodeCountRef.current > 0
      )) {
        console.warn('🛡️ syncSave: 检测到空节点，跳过保存');
        return;
      }

      const cleanNodes = rawNodes.map(node => {
        const { selected, positionAbsolute, dragging, ...rest } = node;
        const { localCacheUrl, uploading, ...rawData } = rest.data || {};
        // 🧹 剥离函数和 ref 对象（JSON.stringify 忽略函数，但 globalAssetsRef 等纯对象会被序列化，膨胀请求体）
        const cleanData = stripNonSerializable(rawData);
        if (cleanData.output && (cleanData.output.startsWith('blob:') || cleanData.output.startsWith('data:'))) {
          cleanData.output = undefined;
          cleanData.outputs = undefined;
        }
        // 🔧 annotationPreview 是 blob URL，不能持久化
        if (cleanData.annotationPreview && cleanData.annotationPreview.startsWith('blob:')) {
          delete cleanData.annotationPreview;
        }
        if (cleanData.refFiles) cleanData.refFiles = cleanData.refFiles.map(({ localCacheUrl: _, ...f }) => f);
        if (cleanData.refAudios) cleanData.refAudios = cleanData.refAudios.map(({ localCacheUrl: _, ...a }) => a);
        return { ...rest, data: cleanData };
      });
      // 🛡️ 包含版本号，防止 beforeunload 保存导致版本号偏移
      const payload = JSON.stringify({
        nodes: cleanNodes,
        edges: getEdges(),
        viewport: getViewport(),
        version: projectVersionRef.current,
        _trigger: 'before_unload',
      });
      const tkn = api.getToken ? api.getToken() : localStorage.getItem('cineart_token');
      fetch(`${C.API_BASE_URL}/api/projects/${pid}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(tkn ? { 'Authorization': `Bearer ${tkn}` } : {})
        },
        body: payload,
        keepalive: true
      }).catch(() => {});
    } catch (e) {
      console.warn('⚠️ 离开保存失败', e);
    }
  };

  // --- 页面离开前保存 + 生成中警告（浏览器关闭/刷新）---
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      syncSaveRef.current?.();
      // 🛡️ 如果有节点正在 AI 生成中，弹出浏览器原生确认对话框
      // 防止用户误刷新导致丢失正在进行的生成任务
      const currentNodes = nodesRef.current;
      const hasLoading = currentNodes?.some(n => n.data?.loading === true);
      if (hasLoading) {
        e.preventDefault();
        e.returnValue = '有 AI 任务正在生成中，刷新页面可能导致结果丢失。确定要离开吗？';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  // --- 组件卸载时保存（React 路由导航离开）---
  // ⚠️ EditorPage 在 /editor/A → /editor/B 之间复用（不会 unmount），
  //   所以本 hook 实例也会跨项目复用。projectId 切换时必须重置跨项目残留的 ref，
  //   否则上一个项目的 dirtyRef=true 会让新项目的 30s interval 立刻 saveProject，
  //   带着 Plan A 缓存里的旧 version 写回服务器（OCC 拦不住，50% 保护也可能绕过）。
  useEffect(() => {
    isMountedRef.current = true;        // 🛡️ StrictMode 重新挂载时重置（mount→unmount→mount）
    activeProjectIdRef.current = projectId; // 🛡️ 记录当前活跃的项目 ID
    // 🛡️ 跨项目重置：避免上一个项目残留的 dirty/lastKnownGoodNodeCount 污染新项目
    dirtyRef.current = false;
    prevNodesRef.current = nodes;
    prevEdgesRef.current = edges;
    lastKnownGoodNodeCountRef.current = 0;
    return () => {
      console.log('🔄 编辑器卸载，执行离开保存...');
      syncSaveRef.current?.();          // 同步保存（用 fetch+keepalive，不经过 saveProject）
      isMountedRef.current = false;     // 🛡️ 标记卸载，阻止后续 setTimeout 触发的幽灵保存
      activeProjectIdRef.current = null; // 🛡️ 清除活跃项目标记
    };
  }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  return { saveProject, syncSaveRef, isMountedRef, activeProjectIdRef };
};

export default useProjectSave;
